import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button, F0Select } from '@factorialco/f0-react'
import { GetTreeNodesByTypeDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { type CustomNode } from 'modules/job_catalog/components/JobCatalogTree/types'
import {
  NODE_TYPES,
  formatUuidToNodeType,
  getTextFromNodeType,
  isNodeType,
  nodeTypeIcon,
} from 'modules/job_catalog/lib/nodesUtils'

import { useBulkMoveJobCatalogTreeNode } from './useBulkMoveJobCatalogTreeNode'

// This is used to show an empty option in the select
// that way we never show the All selected option
const EMPTY_OPTION = {
  label: '',
  value: '',
}

interface Props {
  nodes: CustomNode[]
  nodesType: string
  parentUuid: string
  onSubmit: (success: boolean) => void
  onClose: () => void
}

const BulkMoveJobCatalogTreeNodeModal = ({
  nodes,
  nodesType,
  parentUuid,
  onSubmit,
  onClose,
}: Props) => {
  const [newParentUuid, setNewParentUuid] = React.useState<string>()
  const { bulkMoveJobCatalogTreeNodes, loading: bulkMoveJobCatalogTreeNodesLoading } =
    useBulkMoveJobCatalogTreeNode()
  const { company } = useCurrentSession()
  const parentNodeType = React.useMemo(() => formatUuidToNodeType(parentUuid), [parentUuid])
  const parentNodeTypeLabel = React.useMemo(
    () => getTextFromNodeType(parentNodeType),
    [parentNodeType]
  )

  const { data, loading: parentsLoadingQuery } = useQuery(GetTreeNodesByTypeDocument, {
    variables: { nodeType: parentNodeType },
    fetchPolicy: 'no-cache',
  })

  const parentsOptions = React.useMemo(
    () =>
      data?.jobCatalog.treeNodesConnection.nodes
        .filter((node) => node.uuid !== parentUuid)
        .map((node) => ({
          label: node.name ?? '',
          value: node.uuid,
        })) ?? [],
    [data, parentUuid]
  )

  const selectedNodesOptions = React.useMemo(() => {
    return nodes.map((node) => ({
      label: node.data.name,
      value: node.id,
    }))
  }, [nodes])

  const selectedNodesValues = React.useMemo(() => {
    return nodes.map((node) => node.id)
  }, [nodes])

  const handleSubmit = async () => {
    if (!newParentUuid) {
      return
    }

    const result = await bulkMoveJobCatalogTreeNodes({
      companyId: company.id,
      newParentUuid,
      nodeUuids: selectedNodesValues,
    })

    const handled = handleResult(result, (resultErrors) => {
      app.setFlash({
        type: 'wrong',
        message: resultErrors.messages || i18n.t('errors.generic_error'),
      })

      onSubmit(false)
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('job_catalog.nodes.bulk_move.success'),
        time: 5000,
      })
      onSubmit(true)
    }
  }

  return (
    <Modal
      title={i18n.t('job_catalog.nodes.bulk_move.title', { nodesType })}
      onClose={onClose}
      footer={
        <Box gap="s16" flexDirection="row" alignItems="center">
          <F0Button label={i18n.t('common.cancel')} onClick={onClose} variant="outline" />
          <F0Button
            label={i18n.t('job_catalog.nodes.move.submit')}
            onClick={handleSubmit}
            loading={bulkMoveJobCatalogTreeNodesLoading}
            disabled={!newParentUuid || bulkMoveJobCatalogTreeNodesLoading || parentsLoadingQuery}
          />
        </Box>
      }
    >
      <Box gap="s24">
        <F0Select
          label={nodesType.charAt(0).toUpperCase() + nodesType.slice(1)}
          multiple
          options={[EMPTY_OPTION, ...selectedNodesOptions]}
          value={selectedNodesValues}
          disabled
        />
        <F0Select
          label={parentNodeTypeLabel}
          placeholder={i18n.t('job_catalog.nodes.bulk_move.select_target_parent.placeholder', {
            nodeType: parentNodeTypeLabel.toLowerCase(),
          })}
          options={parentsOptions}
          icon={nodeTypeIcon(isNodeType(parentNodeType) ? parentNodeType : NODE_TYPES.ROOT)}
          loading={parentsLoadingQuery}
          onChange={(value: string) => setNewParentUuid(value)}
        />
      </Box>
    </Modal>
  )
}

export default BulkMoveJobCatalogTreeNodeModal
